import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { atLeast } from "@/lib/roles";
import { listAppRenewals, closeAppRenewal } from "@/lib/api";
import { fmtMoneyWith } from "@/lib/money";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RefreshCw, Smartphone } from "lucide-react";

const STATUS = {
  paid: { label: "Paid · in All Sales", cls: "bg-emerald-500/10 text-emerald-300" },
  pending: { label: "Paying now", cls: "bg-sky-500/10 text-sky-300" },
  requested: { label: "Waiting for the desk", cls: "bg-amber-500/10 text-amber-300" },
  cash_waiting: { label: "Paying cash at the desk", cls: "bg-amber-500/10 text-amber-300" },
  cash_claimed: { label: "Cash taken · save it in Cash Payments", cls: "bg-amber-500/10 text-amber-300" },
  done: { label: "Done at the desk", cls: "bg-emerald-500/10 text-emerald-300" },
  expired: { label: "Not paid (expired)", cls: "bg-zinc-500/10 text-zinc-400" },
  failed: { label: "Payment failed", cls: "bg-red-500/10 text-red-300" },
  cancelled: { label: "Cancelled", cls: "bg-zinc-500/10 text-zinc-400" },
  replaced: { label: "Started again", cls: "bg-zinc-500/10 text-zinc-500" },
};
const fmtDay = (d) => (d ? new Date(d.slice(0, 10) + "T00:00:00").toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) : "—");

export default function AppRenewals() {
  const { activeFacility, effectiveRole, user } = useAuth();
  const [data, setData] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await listAppRenewals());
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load app renewals");
    }
  }, []);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const close = async (o, outcome) => {
    const q = outcome === "done"
      ? `Mark ${o.name}'s renewal as done? Enter the sale in All Sales as usual; the ${o.quote.points} points stay used.`
      : `Cancel ${o.name}'s request? Their ${o.quote.points} points go back.`;
    if (!window.confirm(q)) return;
    try {
      await closeAppRenewal(o.id, outcome);
      toast.success(outcome === "done" ? "Marked done" : "Request cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  if (!atLeast(effectiveRole || user?.role, "manager")) return <div className="card-surface p-8 text-sm text-zinc-500">Managers only.</div>;
  const quiet = ["expired", "cancelled", "replaced", "failed"];
  const orders = (data?.orders || []).filter((o) => showAll || !quiet.includes(o.status));
  const waiting = (data?.orders || []).filter((o) => o.status === "requested").length;

  return (
    <div className="space-y-6" data-testid="app-renewals-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Retention</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">App Renewals</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
            Members renewing from the LIFTR app, with reward points off the price. Paid renewals are added to All Sales
            automatically (status Renewal, paid by {data?.account ? data.account.label : "the gateway"}), so there&apos;s
            nothing to enter.
            {data?.method === "desk" && " This gym has no payment account yet, so members send a request: take payment at the desk, enter the sale, then mark it done."}
          </p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {data && (
        <div className={`rounded-lg border px-4 py-3 text-sm ${data.method === "online" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-amber-500/30 bg-amber-500/10 text-amber-200"}`}
          data-testid="app-renewals-method">
          {data.method === "online"
            ? `${activeFacility?.name || "This gym"} takes payments in the app through ${data.account.label}${data.account.mode === "test" ? " (test mode: no real money)" : ""}.`
            : `${activeFacility?.name || "This gym"} takes renewal requests in the app; payment at the front desk.${waiting ? ` ${waiting} waiting.` : ""}`}
        </div>
      )}

      <div className="card-surface overflow-x-auto">
        {!data ? <div className="p-8 text-center text-sm text-zinc-500">Loading…</div> : orders.length === 0 ? (
          <div className="p-10 text-center text-sm text-zinc-500">
            <Smartphone className="mx-auto mb-2 h-5 w-5 text-zinc-600" /> No renewals from the app yet.
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="app-renewals-table">
            <thead>
              <tr className="border-b border-[#1f1f1f] text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                <th className="px-4 py-3">Member</th>
                <th className="px-4 py-3">Plan</th>
                <th className="px-4 py-3 text-right">Points</th>
                <th className="px-4 py-3 text-right">Total</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => {
                const st = STATUS[o.status] || { label: o.status, cls: "bg-zinc-500/10 text-zinc-400" };
                return (
                  <tr key={o.id} className="border-b border-[#161616] last:border-0" data-testid={`app-renewal-${o.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-zinc-100">{o.name || "—"}</div>
                      <div className="text-[11px] text-zinc-500">{o.customer_key} · {new Date(o.created_at).toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-zinc-200">{o.programme}</div>
                      <div className="text-[11px] text-zinc-500">{fmtDay(o.start_date)} → {fmtDay(o.end_date)}</div>
                    </td>
                    <td className="px-4 py-3 text-right text-zinc-300">
                      {o.quote.points ? <>{o.quote.points}<div className="text-[11px] text-zinc-500">−{fmtMoneyWith(o.currency_symbol, o.quote.discount)}</div></> : "—"}
                    </td>
                    <td className="px-4 py-3 text-right font-medium text-zinc-100">{fmtMoneyWith(o.currency_symbol, o.quote.total)}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${st.cls}`}>{st.label}</span>
                      {o.closed_by && <div className="mt-1 text-[10px] text-zinc-500">by {o.closed_by}</div>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {o.status === "requested" && (
                        <div className="flex justify-end gap-2">
                          <Button size="sm" onClick={() => close(o, "done")} data-testid={`app-renewal-done-${o.id}`}>Done</Button>
                          <Button size="sm" variant="outline" onClick={() => close(o, "cancelled")}>Cancel</Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {data?.orders?.some((o) => quiet.includes(o.status)) && (
        <button type="button" className="text-xs text-zinc-500 underline" onClick={() => setShowAll(!showAll)}>
          {showAll ? "Hide unpaid and cancelled" : "Show unpaid and cancelled too"}
        </button>
      )}
    </div>
  );
}
